// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';

import { withPageMeta } from '@gandi/app-runner';
import { getCartUUIDFromOrderId } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { Base, Button, Heading } from '@gandi/ui';

import { CallToAction } from 'src/Overview/components/CallToAction';
import { Layout } from 'src/components/Layout';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectOrderUrl } from 'src/modules/config';

import { AvailabilityIcon } from '../Domain/AvailabilityIcon';

import styles from './AwaitingPayment.scss';

export type Params = {
  orderId: string,
};

function AwaitingPayment() {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const params = useParams<Params>();

  const [cartId, setCartId] = React.useState<string>('');
  const orderUrl = useAppSelector(state =>
    selectOrderUrl(state, { orderId: params.orderId }),
  );

  React.useEffect(() => {
    if (params.orderId) {
      dispatch(getCartUUIDFromOrderId(params.orderId)).then(resp => {
        const cartUUID = resp.data?.[0]?.uuid;
        if (!cartUUID) {
          return;
        }
        setCartId(cartUUID);
      });
    }
  }, [dispatch, params.orderId]);

  return (
    <Layout backgroundColor="surface" currentStep={4}>
      <div className={styles.wrapper}>
        <div className={styles.root}>
          <AvailabilityIcon iconName="warning" className={styles.icon} />
          <Base className={styles.container}>
            <div className={styles.content}>
              <Heading as="h1">
                {__('Your order is now awaiting payment!')}
              </Heading>

              <Base tagName="p" marginTop="none">
                {__(
                  'Your order is now available in the Billing section of your dashboard. If this order contains domain names, please note that no operation can be launched until your payment has been made and validated.',
                )}
              </Base>
            </div>
            <div className={styles.buttons}>
              <Button kind="outline" href={orderUrl} grouped>
                {__('View order')}
              </Button>

              {cartId && (
                <Button to={`/cart/${cartId}/checkout/${params.orderId}`}>
                  {__('Pay now')}
                </Button>
              )}
            </div>
          </Base>
        </div>
        <CallToAction />
      </div>
    </Layout>
  );
}

export default (compose(
  withPageMeta(args => ({
    title: args.__('Order awaiting payment - Gandi.net'),
  })),
)(AwaitingPayment): React.ComponentType<{}>);
